import { LogOut, Menu } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { useAuth } from "@/auth/AuthContext";
import { GlobalSearch } from "@/components/GlobalSearch";
import { ChangePasswordModal } from "@/components/ChangePasswordModal";
import { useState } from "react";
import { colors, semanticColors, layout, heights, spacing, radius, typography, iconSize, iconStroke, shadows } from "@/styles/tokens";

// Initials for the avatar, from the user's own name rather than a constant.
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

export function TopBar({ phone = false, menuOpen: navOpen = false, onMenu }: {
  /** Phone layout: the sidebar is a slide-in menu, opened from a button here. */
  phone?: boolean;
  menuOpen?: boolean;
  onMenu?: () => void;
}) {
  const { user, role, signOut } = useAuth();
  // password-reset: the profile menu (the avatar) and its one item.
  const [menuOpen, setMenuOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  return (
    <header
      data-testid="app-topbar"
      className="flex items-center bg-white shrink-0"
      style={{ height: heights.topBar, borderBottom: `1px solid ${semanticColors.border}`, paddingLeft: spacing[16], paddingRight: spacing[16] }}
    >
      {/* The brand lives in the SIDEBAR header now (sidebar-and-logo). On a
          phone the sidebar is a slide-in menu, so the top bar carries the menu
          button and the small mark instead. */}
      {phone && (
        <div className="flex items-center" style={{ gap: spacing[8], marginRight: spacing[8], flexShrink: 0 }}>
          <button
            type="button"
            data-testid="menu-button"
            aria-label={navOpen ? "Close menu" : "Open menu"}
            aria-expanded={navOpen}
            aria-controls="app-sidebar"
            onClick={onMenu}
            style={{
              width: heights.avatarSearch, height: heights.avatarSearch, padding: 0,
              border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
              backgroundColor: colors.white, color: semanticColors.textSecondary, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Menu size={iconSize.md} strokeWidth={iconStroke.regular} />
          </button>
          <img src={BRAND.logo} alt={BRAND.markAlt} style={{ width: heights.topBarLogo, height: "auto", display: "block" }} />
        </div>
      )}

      {/* The global search (global-search, 2026-09-30). It replaced an
          UNCONTROLLED <input> that searched nothing; this one searches quotes,
          customers and products through the same three reads the lists use,
          so its scoping is the server's. See GlobalSearch.tsx. */}
      <div className="flex-1" />
      <GlobalSearch />
      <div className="flex-1" />

      <div
        className="flex items-center justify-end"
        style={{ minWidth: phone ? 0 : layout.topBarAccountMinWidth, gap: phone ? spacing[6] : spacing[10] }}
      >
        {user && !phone && (
          <div className="text-right leading-tight" style={{ minWidth: 0 }}>
            <div
              data-testid="app-user-name"
              style={{
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
                color: semanticColors.textPrimary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {user.name}
            </div>
            {/* The role is shown because it explains why controls are missing.
                A Viewer who cannot see Save should be able to tell at a glance
                that it is their role, not a broken page. */}
            <div
              data-testid="app-user-role"
              style={{
                fontSize: typography.fontSize.xs,
                color: role ? semanticColors.textMuted : semanticColors.destructive,
                textTransform: "capitalize",
              }}
            >
              {role ?? "No role assigned"}
            </div>
          </div>
        )}

        <div style={{ position: "relative" }}>
        <button
          type="button"
          data-testid="app-profile-button"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          title="Profile"
          onClick={() => setMenuOpen((v) => !v)}
          style={{ padding: 0, border: "none", background: "transparent", cursor: "pointer", display: "block" }}
        >
        <div
          data-testid="app-avatar"
          className="flex items-center justify-center shrink-0"
          style={{
            width: heights.avatarSearch,
            height: heights.avatarSearch,
            borderRadius: radius.full,
            backgroundColor: colors.indigo600,
            // ⚠️ NOT white. The accent is a light green now, and white on it
            // is 1.6:1 — this avatar sits in the top bar on every screen, so
            // it was the most-seen unreadable thing in the app. onAccent is
            // the same dark ink `.btn-primary` uses, at 12.5:1.
            color: semanticColors.onAccent,
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
          }}
          aria-label="Account"
        >
          {user ? initials(user.name) : "?"}
        </div>
        </button>
        {menuOpen && (
          <div data-testid="app-profile-menu" role="menu"
               style={{
                 position: "absolute", right: 0, top: heights.avatarSearch + spacing[4], zIndex: 50,
                 minWidth: 200, backgroundColor: colors.white, border: `1px solid ${semanticColors.border}`,
                 borderRadius: radius.base, boxShadow: shadows.modal, padding: spacing[4],
               }}>
            <button type="button" role="menuitem" data-testid="profile-change-password"
                    onClick={() => { setMenuOpen(false); setChangingPassword(true); }}
                    style={{
                      display: "block", width: "100%", textAlign: "left", padding: `${spacing[6]}px ${spacing[8]}px`,
                      border: "none", background: "transparent", cursor: "pointer",
                      fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
                    }}>
              Change my password
            </button>
          </div>
        )}
        </div>
        {changingPassword && <ChangePasswordModal onClose={() => setChangingPassword(false)} />}

        <button
          type="button"
          data-testid="app-signout"
          onClick={() => { void signOut(); }}
          title="Sign out"
          aria-label="Sign out"
          className="btn-secondary flex items-center justify-center shrink-0"
          style={{
            height: heights.avatarSearch,
            width: heights.avatarSearch,
            border: `1px solid ${semanticColors.border}`,
            borderRadius: radius.base,
            backgroundColor: colors.white,
            color: semanticColors.textSecondary,
            cursor: "pointer",
          }}
        >
          <LogOut size={iconSize.md} strokeWidth={iconStroke.regular} />
        </button>
      </div>
    </header>
  );
}
